<script setup lang="ts">
/**
 * Detail body — renders the full transaction context with stacked
 * sections (Transaction → Investor → Settlement → Related entity).
 * Empty rows + empty sections are hidden per the kit's "hide empty
 * fields" rule.
 *
 * The related-entity section only renders for `referenceType=order`
 * today (admin-web has a dedicated orders detail route). Other
 * reference types surface their context inside the Settlement section
 * (recharge / withdraw) — no separate entity page exists yet.
 */
import { computed } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'

import { formatMoney, formatKey } from '@shared/utils'
import { formatDateTime } from '@shared/utils/dateTimeUtils'

import FiatTransactionTypeBadge from './FiatTransactionTypeBadge.vue'
import {
  FIAT_TRANSACTION_REFERENCE_TYPE,
  FIAT_TRANSACTION_STATUS,
  type FiatTransactionDetail,
} from '../types/fiatTransactionsTypes'
import {
  referenceTypeColor,
  referenceTypeLabel,
  transactionReasonLabel,
  transactionStatusColor,
  transactionStatusLabel,
} from '../utils/fiatTransactionLabels'

const props = defineProps<{
  details: FiatTransactionDetail
}>()

const emit = defineEmits<{
  'open-order': [orderId: string]
}>()

const investorFullName = computed(() => {
  const first = props.details.investor.firstName ?? ''
  const last = props.details.investor.lastName ?? ''
  return `${first} ${last}`.trim()
})

const amountLabel = computed(() => formatMoney(props.details.currencyCode, props.details.amount))

// Settled ledger rows carry balance snapshots; rejected requests never moved
// money, so their balances are absent (null) / meaningless and stay hidden.
const hasBalances = computed(
  () =>
    props.details.status === FIAT_TRANSACTION_STATUS.COMPLETED &&
    props.details.balanceBefore !== null &&
    props.details.balanceAfter !== null,
)

const balanceBeforeLabel = computed(() =>
  formatMoney(props.details.currencyCode, props.details.balanceBefore ?? '0'),
)

const balanceAfterLabel = computed(() =>
  formatMoney(props.details.currencyCode, props.details.balanceAfter ?? '0'),
)

const recharge = computed(() => props.details.settlement.recharge)
const withdraw = computed(() => props.details.settlement.withdraw)

const showSettlementSection = computed(() => recharge.value !== null || withdraw.value !== null)

const showRelatedOrderSection = computed(
  () =>
    props.details.referenceType === FIAT_TRANSACTION_REFERENCE_TYPE.ORDER &&
    !!props.details.referenceId,
)

function handleOpenOrder() {
  if (!props.details.referenceId) return
  emit('open-order', props.details.referenceId)
}

function handleOpenDocument(url: string | null | undefined) {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <!-- TRANSACTION -->
    <Box class="section section--lead">
      <Box flex direction="col" gap="3">
        <Box flex direction="col" gap="1">
          <Text type="caption" color="muted">Amount</Text>
          <Box flex align="center" gap="3">
            <Text type="display" weight="bold" style="font-variant-numeric: tabular-nums">
              {{ amountLabel }}
            </Text>
            <FiatTransactionTypeBadge :type="details.transactionType" />
          </Box>
        </Box>

        <Box flex direction="row" gap="2" align="center">
          <Text type="body-sm" color="muted">{{
            transactionReasonLabel(details.transactionReason)
          }}</Text>
          <Text type="body-sm" color="muted">·</Text>
          <StatusBadge
            :color="referenceTypeColor(details.referenceType)"
            :label="referenceTypeLabel(details.referenceType)"
          />
        </Box>

        <Box flex direction="col" gap="2" class="rows">
          <Box flex justify="between">
            <Text type="body-sm" color="muted">Submitted at</Text>
            <Text type="body-sm" weight="medium">{{ formatDateTime(details.createdAt) }}</Text>
          </Box>
          <!-- Balances only exist for settled ledger rows. A rejected request
               never moved money, so we hide the (meaningless) 0.00 snapshots. -->
          <template v-if="hasBalances">
            <Box flex justify="between">
              <Text type="body-sm" color="muted">Balance before</Text>
              <Text type="body-sm" weight="medium" style="font-variant-numeric: tabular-nums">
                {{ balanceBeforeLabel }}
              </Text>
            </Box>
            <Box flex justify="between">
              <Text type="body-sm" color="muted">Balance after</Text>
              <Text type="body-sm" weight="medium" style="font-variant-numeric: tabular-nums">
                {{ balanceAfterLabel }}
              </Text>
            </Box>
          </template>
          <Box flex justify="between" align="center">
            <Text type="body-sm" color="muted">Status</Text>
            <StatusBadge
              :color="transactionStatusColor(details.status)"
              :label="transactionStatusLabel(details.status)"
            />
          </Box>
          <Box
            v-if="details.status === FIAT_TRANSACTION_STATUS.REJECTED && details.rejectionReason"
            flex
            justify="between"
            gap="3"
          >
            <Text type="body-sm" color="muted">Rejection reason</Text>
            <Text type="body-sm" weight="medium" align="right">{{ details.rejectionReason }}</Text>
          </Box>
          <Box v-if="details.externalReferenceId" flex justify="between" align="center" gap="2">
            <Text type="body-sm" color="muted">External reference</Text>
            <Box flex align="center" gap="2">
              <Text type="body-sm" weight="medium">{{ details.externalReferenceId }}</Text>
              <CopyButton :value="details.externalReferenceId" label="Copy" copied-label="Copied" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>

    <!-- INVESTOR -->
    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Investor</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="investorFullName" flex justify="between">
          <Text type="body-sm" color="muted">Name</Text>
          <Text type="body-sm" weight="medium">{{ investorFullName }}</Text>
        </Box>
        <Box v-if="details.investor.email" flex justify="between">
          <Text type="body-sm" color="muted">Email</Text>
          <Text type="body-sm" weight="medium">{{ details.investor.email }}</Text>
        </Box>
        <Box v-if="details.investor.kycStatus" flex justify="between">
          <Text type="body-sm" color="muted">KYC</Text>
          <StatusBadge
            :color="details.investor.kycStatus === 'approved' ? 'success' : 'warning'"
            :label="formatKey(details.investor.kycStatus)"
          />
        </Box>
      </Box>
    </Box>

    <!-- SETTLEMENT — RECHARGE -->
    <Box v-if="showSettlementSection && recharge" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Recharge settlement</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.treasuryBankAccount" flex justify="between">
          <Text type="body-sm" color="muted">Treasury account</Text>
          <Text type="body-sm" weight="medium" align="right">
            {{ details.treasuryBankAccount.accountName }}
            <template v-if="details.treasuryBankAccount.bankName">
              · {{ details.treasuryBankAccount.bankName }}
            </template>
            <template v-if="details.treasuryBankAccount.currencyCode">
              · {{ details.treasuryBankAccount.currencyCode }}
            </template>
          </Text>
        </Box>
        <Box v-if="details.treasuryBankAccount?.iban" flex justify="between">
          <Text type="body-sm" color="muted">Treasury IBAN</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.iban }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Status</Text>
          <Text type="body-sm" weight="medium">{{ formatKey(recharge.status) }}</Text>
        </Box>
        <Box v-if="recharge.paymentReference" flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Payment reference</Text>
          <Box flex align="center" gap="2">
            <Text type="body-sm" weight="medium">{{ recharge.paymentReference }}</Text>
            <CopyButton :value="recharge.paymentReference" label="Copy" copied-label="Copied" />
          </Box>
        </Box>
        <Box v-if="recharge.approvedBankReference" flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Approved bank reference</Text>
          <Box flex align="center" gap="2">
            <Text type="body-sm" weight="medium">{{ recharge.approvedBankReference }}</Text>
            <CopyButton
              :value="recharge.approvedBankReference"
              label="Copy"
              copied-label="Copied"
            />
          </Box>
        </Box>
        <Box v-if="recharge.documentUrl" flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Document</Text>
          <Button
            label="Open in new tab"
            icon="external-link"
            icon-pos="right"
            variant="link"
            color="primary"
            @click="handleOpenDocument(recharge.documentUrl)"
          />
        </Box>
        <Box v-if="recharge.completedAt" flex justify="between">
          <!-- "Completed at" not "Approved at" — BE column gap; `approvedAt` is
               null for recharge today. -->
          <Text type="body-sm" color="muted">Completed at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(recharge.completedAt) }}</Text>
        </Box>
        <Box v-if="recharge.rejectionReason" flex justify="between" gap="3">
          <Text type="body-sm" color="muted">Rejection reason</Text>
          <Text type="body-sm" weight="medium" align="right">{{ recharge.rejectionReason }}</Text>
        </Box>
      </Box>
    </Box>

    <!-- SETTLEMENT — WITHDRAW -->
    <Box v-if="showSettlementSection && withdraw" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Withdrawal settlement</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.treasuryBankAccount" flex justify="between">
          <Text type="body-sm" color="muted">Treasury account</Text>
          <Text type="body-sm" weight="medium" align="right">
            {{ details.treasuryBankAccount.accountName }}
            <template v-if="details.treasuryBankAccount.bankName">
              · {{ details.treasuryBankAccount.bankName }}
            </template>
            <template v-if="details.treasuryBankAccount.currencyCode">
              · {{ details.treasuryBankAccount.currencyCode }}
            </template>
          </Text>
        </Box>
        <Box v-if="details.treasuryBankAccount?.iban" flex justify="between">
          <Text type="body-sm" color="muted">Treasury IBAN</Text>
          <Text type="body-sm" weight="medium">{{ details.treasuryBankAccount.iban }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Status</Text>
          <Text type="body-sm" weight="medium">{{ formatKey(withdraw.status) }}</Text>
        </Box>
        <Box v-if="withdraw.destinationBankAccount.bankName" flex justify="between">
          <Text type="body-sm" color="muted">Destination bank</Text>
          <Text type="body-sm" weight="medium">{{ withdraw.destinationBankAccount.bankName }}</Text>
        </Box>
        <Box v-if="withdraw.destinationBankAccount.accountHolderName" flex justify="between">
          <Text type="body-sm" color="muted">Account holder</Text>
          <Text type="body-sm" weight="medium">
            {{ withdraw.destinationBankAccount.accountHolderName }}
          </Text>
        </Box>
        <Box v-if="withdraw.destinationBankAccount.accountNumberMasked" flex justify="between">
          <Text type="body-sm" color="muted">Account number</Text>
          <Text type="body-sm" weight="medium">
            {{ withdraw.destinationBankAccount.accountNumberMasked }}
          </Text>
        </Box>
        <Box v-if="withdraw.destinationBankAccount.iban" flex justify="between">
          <Text type="body-sm" color="muted">IBAN</Text>
          <Text type="body-sm" weight="medium">{{ withdraw.destinationBankAccount.iban }}</Text>
        </Box>
        <Box v-if="withdraw.settlementReference" flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Settlement reference</Text>
          <Box flex align="center" gap="2">
            <Text type="body-sm" weight="medium">{{ withdraw.settlementReference }}</Text>
            <CopyButton :value="withdraw.settlementReference" label="Copy" copied-label="Copied" />
          </Box>
        </Box>
        <Box v-if="withdraw.settledAt" flex justify="between">
          <Text type="body-sm" color="muted">Settled at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(withdraw.settledAt) }}</Text>
        </Box>
        <Box v-if="withdraw.approvedAt" flex justify="between">
          <Text type="body-sm" color="muted">Approved at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(withdraw.approvedAt) }}</Text>
        </Box>
        <Box v-if="withdraw.approvedBy" flex justify="between">
          <Text type="body-sm" color="muted">Approved by</Text>
          <Text type="body-sm" weight="medium">{{ withdraw.approvedBy }}</Text>
        </Box>
        <Box v-if="withdraw.rejectionReason" flex justify="between" gap="3">
          <Text type="body-sm" color="muted">Rejection reason</Text>
          <Text type="body-sm" weight="medium" align="right">{{ withdraw.rejectionReason }}</Text>
        </Box>
      </Box>
    </Box>

    <!-- RELATED ENTITY (orders only, today) -->
    <Box v-if="showRelatedOrderSection && details.referenceId" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Related order</Text
      >
      <Box flex direction="col" gap="3" class="rows">
        <Box flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Order id</Text>
          <Box flex align="center" gap="2">
            <Text type="body-sm" weight="medium" class="mono">{{ details.referenceId }}</Text>
            <CopyButton :value="details.referenceId" label="Copy" copied-label="Copied" />
          </Box>
        </Box>
        <Button
          label="View order details"
          icon="arrow-right"
          icon-pos="right"
          color="primary"
          @click="handleOpenOrder"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.section {
  border-top: 1px solid var(--p-content-border-color);
  padding-top: 1rem;
}

.section.section--lead {
  border-top: none;
  padding-top: 0;
}

.section-label {
  display: block;
  margin-bottom: 0.5rem;
}

.rows {
  padding-top: 0.25rem;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
</style>
